import { alpha, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";

import type { WearableAggregationMode } from "./wearableMetricAggregation";

interface WearableAggregationToggleProps {
  value: WearableAggregationMode;
  onChange: (mode: WearableAggregationMode) => void;
  tone?: "default" | "onPrimary";
  /** Cor de destaque em formato CSS (#rrggbb). Se omitido, usa primary do tema. */
  color?: string;
}

export function WearableAggregationToggle({
  value,
  onChange,
  tone = "default",
  color,
}: WearableAggregationToggleProps) {
  const theme = useTheme();
  const isOnPrimary = tone === "onPrimary";
  // alpha() exige cor CSS real — "primary.main" quebra (path do tema, não valor).
  const accent = color || theme.palette.primary.main;

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_event, next: WearableAggregationMode | null) => {
        if (next) onChange(next);
      }}
      onClick={(event) => event.stopPropagation()}
      sx={{
        flexShrink: 0,
        bgcolor: isOnPrimary
          ? (t) => alpha(t.palette.common.white, 0.1)
          : "background.paper",
        "& .MuiToggleButton-root": {
          px: 1,
          py: 0.25,
          minWidth: 52,
          fontSize: "0.72rem",
          fontWeight: 600,
          lineHeight: 1.2,
          textTransform: "none",
          ...(isOnPrimary
            ? {
                color: "white",
                borderColor: (t) => alpha(t.palette.common.white, 0.35),
                "&.Mui-selected": {
                  color: "white",
                  bgcolor: (t) => alpha(t.palette.common.white, 0.22),
                  borderColor: (t) => alpha(t.palette.common.white, 0.85),
                  "&:hover": {
                    bgcolor: (t) => alpha(t.palette.common.white, 0.28),
                  },
                },
                "&:hover": {
                  bgcolor: (t) => alpha(t.palette.common.white, 0.14),
                },
              }
            : {
                borderColor: alpha(accent, 0.25),
                color: "text.secondary",
                "&.Mui-selected": {
                  color: accent,
                  bgcolor: alpha(accent, 0.1),
                  borderColor: alpha(accent, 0.33),
                  "&:hover": {
                    bgcolor: alpha(accent, 0.14),
                  },
                },
                "&:hover": {
                  bgcolor: alpha(accent, 0.06),
                },
              }),
        },
      }}
    >
      <ToggleButton value="sum">Total</ToggleButton>
      <ToggleButton value="average">Média</ToggleButton>
    </ToggleButtonGroup>
  );
}
